import { useState, useEffect, useCallback, createContext, useContext, ReactNode, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearTenantContext, ensureTenantContextOwner } from "@/lib/tenant-context";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  lastAuthEvent: string;
  lastAuthReason: string;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_CACHE_KEYS = [
  "fastblog_company_role",
  "fastblog_super_admin",
  "fastblog_master_admin",
  "fastblog_active_tenant",
  "fastblog_active_project",
];

const AUTH_DEBUG_EVENT_KEY = "fastblog_auth_last_event";
const AUTH_DEBUG_REASON_KEY = "fastblog_auth_last_reason";
const AUTH_REDIRECT_REASON_KEY = "fastblog_auth_redirect_reason";

function clearAuthCaches() {
  try {
    AUTH_CACHE_KEYS.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

function persistDebug(event: string, reason: string) {
  try {
    sessionStorage.setItem(AUTH_DEBUG_EVENT_KEY, event);
    sessionStorage.setItem(AUTH_DEBUG_REASON_KEY, reason);
  } catch {
    /* ignore */
  }
}

function clearRedirectReason() {
  try {
    sessionStorage.removeItem(AUTH_REDIRECT_REASON_KEY);
  } catch {
    /* ignore */
  }
}

let lastKnownSession: Session | null = null;

// Conferência de login compartilhada: o getUser de rede roda uma vez por token
// a cada 60 s, e chamadas simultâneas esperam a mesma resposta.
let verifiedToken: { token: string; userId: string; at: number } | null = null;
let inflight: { token: string; promise: Promise<boolean> } | null = null;
const VERIFY_TTL_MS = 60_000;

export async function ensureSupabaseAuthSession(candidate?: Session | null): Promise<boolean> {
  try {
    let { data, error } = await supabase.auth.getSession();
    if (error || !data.session?.user) return false;
    if (candidate?.user?.id && data.session.user.id !== candidate.user.id) return false;
    if (data.session.expires_at && data.session.expires_at * 1000 <= Date.now() + 30_000) {
      const refreshed = await supabase.auth.refreshSession();
      if (refreshed.error || !refreshed.data.session?.user) return false;
      data = refreshed.data;
    }
    const session = data.session!;
    const token = session.access_token;
    if (verifiedToken && verifiedToken.token === token && verifiedToken.userId === session.user.id
      && Date.now() - verifiedToken.at < VERIFY_TTL_MS) {
      lastKnownSession = session;
      return true;
    }
    if (!inflight || inflight.token !== token) {
      const promise = (async () => {
        try {
          const verified = await supabase.auth.getUser(token);
          if (verified.error || verified.data.user?.id !== session.user.id) {
            verifiedToken = null;
            return false;
          }
          verifiedToken = { token, userId: session.user.id, at: Date.now() };
          return true;
        } catch {
          return false;
        } finally {
          if (inflight?.token === token) inflight = null;
        }
      })();
      inflight = { token, promise };
    }
    const ok = await inflight.promise;
    if (ok) lastKnownSession = session;
    return ok;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastAuthEvent, setLastAuthEvent] = useState("BOOT");
  const [lastAuthReason, setLastAuthReason] = useState("initializing");
  const explicitSignOutRef = useRef(false);
  const currentSessionRef = useRef<Session | null>(null);

  const mark = useCallback((event: string, reason: string) => {
    setLastAuthEvent(event);
    setLastAuthReason(reason);
    persistDebug(event, reason);
  }, []);

  const clearAuthState = useCallback((reason = "signed_out") => {
    currentSessionRef.current = null;
    lastKnownSession = null;
    setUser(null);
    setSession(null);
    setIsLoading(false);
    clearAuthCaches();
    clearTenantContext();
    mark("SIGNED_OUT", reason);
  }, [mark]);

  const applySession = useCallback((nextSession: Session, reason: string) => {
    const previousUserId = currentSessionRef.current?.user?.id;
    if (previousUserId && previousUserId !== nextSession.user.id) clearAuthCaches();
    ensureTenantContextOwner(nextSession.user.id);
    currentSessionRef.current = nextSession;
    lastKnownSession = nextSession;
    setSession(nextSession);
    setUser(nextSession.user);
    setIsLoading(false);
    clearRedirectReason();
    mark("SESSION_ACTIVE", reason);
  }, [mark]);

  const validateStoredSession = useCallback(async (source: string): Promise<boolean> => {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const storedSession = sessionData.session;

      if (sessionError) {
        console.warn(`[useAuth] getSession error from ${source}:`, sessionError.message);
      }

      if (storedSession?.user && await ensureSupabaseAuthSession(storedSession)) {
        const { data: verifiedSession } = await supabase.auth.getSession();
        if (verifiedSession.session?.user) {
          applySession(verifiedSession.session, `${source}:verified`);
          return true;
        }
      }

      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        const msg = userError.message?.toLowerCase() || "";
        const isExpectedNoSession = msg.includes("session") || msg.includes("jwt") || msg.includes("missing");
        if (!isExpectedNoSession) {
          console.warn(`[useAuth] getUser error from ${source}:`, userError.message);
        }
      }

      if (userData.user) {
        const { data: refreshed } = await supabase.auth.getSession();
        if (refreshed.session?.user) {
          applySession(refreshed.session, `${source}:getUser`);
          return true;
        }
      }
    } catch (error) {
      console.warn(`[useAuth] validation failed from ${source}:`, error);
    }

    return false;
  }, [applySession]);

  const handleSignOut = useCallback(async () => {
    explicitSignOutRef.current = true;
    mark("SIGN_OUT_REQUESTED", "user_action");
    const { error } = await supabase.auth.signOut();
    if (error) {
      await supabase.auth.signOut({ scope: "local" });
    }
    clearAuthState("user_action");
  }, [clearAuthState, mark]);

  useEffect(() => {
    let mounted = true;
    let initialResolved = false;

    const finishSignedOutIfStillEmpty = async (source: string) => {
      if (explicitSignOutRef.current) {
        clearAuthState("user_action");
        return;
      }
      const hasSession = await validateStoredSession(source);
      if (!mounted) return;
      if (!hasSession) {
        setUser(null);
        setSession(null);
        setIsLoading(false);
        mark("NO_SESSION", source);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      mark(event, nextSession?.user ? "event_session_present" : "event_session_null");

      if (nextSession?.user) {
        initialResolved = true;
        applySession(nextSession, event);
        return;
      }

      if (event === "SIGNED_OUT") {
        initialResolved = true;
        setTimeout(() => void finishSignedOutIfStillEmpty("SIGNED_OUT_VALIDATED"), 0);
        return;
      }

      if (event === "INITIAL_SESSION") {
        initialResolved = true;
        setTimeout(() => void finishSignedOutIfStillEmpty("INITIAL_SESSION_VALIDATED"), 0);
      }
    });

    void validateStoredSession("BOOTSTRAP")
      .then((hasSession) => {
        if (!mounted || hasSession) return;
        if (!initialResolved) return;
        setIsLoading(false);
      })
      .catch(() => {
        if (mounted && initialResolved) setIsLoading(false);
      });

    const revalidateOnResume = () => {
      if (!mounted || document.visibilityState !== "visible") return;
      void validateStoredSession("TAB_RESUME").then((hasSession) => {
        if (mounted && !hasSession) clearAuthState("resume_invalid_session");
      });
    };
    document.addEventListener("visibilitychange", revalidateOnResume);
    window.addEventListener("focus", revalidateOnResume);

    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", revalidateOnResume);
      window.removeEventListener("focus", revalidateOnResume);
      subscription.unsubscribe();
    };
  }, [applySession, clearAuthState, mark, validateStoredSession]);

  return (
    <AuthContext.Provider value={{ user, session, isLoading, lastAuthEvent, lastAuthReason, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
